import {
  all,
  fork,
  delay,
  put,
  call,
  select,
  takeLatest,
  take,
  race,
  takeLeading,
} from 'redux-saga/effects';
import * as Actions from './actions';
import dayjs from 'dayjs';
import * as requests from '../../common/helpers/HTTPRequest';
import ApiConstants from '../../common/helpers/ApiConstants';
import { checkValidToken } from 'src/common/helpers/CheckAuth';
import TextConstants from 'src/common/helpers/TextConstants';
import { toastWarning } from 'src/common/helpers/Notifications';
import {
  unitsChannelsActions,
  fetchUnits,
  backgroundFetchDeviceChannels,
  fetchDeviceChannels,
} from 'src/common/store/unitsChannels/actions';
import { COLORS, POINT_MAX } from './constants';
import { getUnitChannels } from './selectors';

function* fetchRecorderView({ payload }) {
  try {
    // check valid token
    yield checkValidToken();

    const {
      dataType,
      isAutoGetOfLatestData,
      TimeRangeOfLatestData,
      from,
      to,
      startTime,
      endTime,
      unit,
      selectedChannels,
      displayValue,
      scalePositions,
      scaleRange,
    } = payload;

    yield put(fetchDeviceChannels({ deviceId: unit }));
    yield race({
      success: take(unitsChannelsActions.FETCH_DEVICE_CHANNELS_SUCCESS),
      error: take(unitsChannelsActions.FETCH_DEVICE_CHANNELS_ERROR),
    });
    const unitChannels = yield select(getUnitChannels);
    const deviceChannelIds = unitChannels.deviceChannels
      .filter((channel) => channel.dataType === 0)
      .map((channel) => channel.channelId);
    const selectedNumberChannelIds = selectedChannels.filter((channelId) =>
      deviceChannelIds.includes(channelId)
    );
    const selectedNumberChannelsInfo =
      unitChannels.deviceChannels.filter((channel) =>
        selectedNumberChannelIds.includes(channel.channelId)
      );

    let chResponses;
    if (dataType === 'raw-data' && isAutoGetOfLatestData) {
      const now = dayjs().valueOf();
      const backtrackHour = Number.parseInt(TimeRangeOfLatestData, 10);

      const timeRanges = [...Array(Math.ceil(backtrackHour / 6)).keys()].map(
        (v) => {
          const end = dayjs(now)
            .subtract(6 * v, 'hours')
            .format('YYYY-MM-DD HH:mm');
          const diff = 6 * (v + 1) > backtrackHour ? backtrackHour - 6 * v : 6;
          const start = dayjs(end)
            .subtract(diff, 'hours')
            .format('YYYY-MM-DD HH:mm');
          return { start, end };
        }
      );

      const urls = timeRanges.map(
        (time) =>
          `${ApiConstants.BASE_URL}${
            ApiConstants.RECORDER
          }?type=${dataType.replace('-data', '')}&start=${time.start}&end=${
            time.end
          }&deviceId=${unit}&channelIds=${selectedNumberChannelIds.join(
            '&channelIds='
          )}`
      );

      /** Response */
      const apiResponses = [];
      for (const url of urls) {
        apiResponses.push(yield call(requests.getRequest, url, {}));
      }

      const chConcatResponses = selectedChannels.map((channelId) => {
        const values = apiResponses.flatMap((res) => res[channelId]);
        return { channelId, values };
      });

      chResponses = Object.assign(
        {},
        ...chConcatResponses.map((r) => ({
          [r.channelId]: r.values,
        }))
      );
    } else {
      let startParam, endParam, dateFormat;
      if (dataType !== 'raw-data') {
        startParam = from;
        endParam = to;
        dateFormat = 'YYYY-MM-DD';
      } else {
        startParam = from + ' ' + startTime;
        endParam = to + ' ' + endTime;
        dateFormat = 'YYYY-MM-DD HH:mm';
      }

      const url = `${ApiConstants.BASE_URL}${
        ApiConstants.RECORDER
      }?type=${dataType.replace('-data', '')}&start=${dayjs(startParam).format(
        dateFormat
      )}&end=${dayjs(endParam).format(
        dateFormat
      )}&deviceId=${unit}&channelIds=${selectedNumberChannelIds.join(
        '&channelIds='
      )}`;

      /** Response */
      chResponses = yield call(requests.getRequest, url, {});
    }
    /** Timestamp array */
    const tArray = [];

    const filteredChResponses = Object.fromEntries(
      Object.entries(chResponses).filter(([channelId, _]) =>
        selectedNumberChannelIds.includes(channelId)
      )
    );
    Object.entries(filteredChResponses).forEach(([_, aggregatedData]) => {
      aggregatedData.forEach((d) => {
        const t = dayjs(d.date || d.timestamp).valueOf();
        tArray.push(t);
      });
    });

    /** Format timestamp array into data for graph display */
    const tdata = Array.from(new Set(tArray)).sort((a, b) => (a > b ? -1 : 1));
    /** data for graph display */
    const newScalePositions = scalePositions.filter((obj) =>
      selectedNumberChannelIds.includes(obj.channelId)
    );
    const cdata = selectedNumberChannelsInfo.map((channelInfo, i) => {
      const scaleInfo = newScalePositions.find(
        (sp) => sp.channelId === channelInfo.channelId
      );
      return {
        index: i,
        name: channelInfo.channelId,
        channelId: channelInfo.channelId,
        channelName: channelInfo.channelName,
        pdata: new Array(tdata.length),
        scalePosition: scaleInfo.scalePosition,
        minScale: Number.parseFloat(
          scaleRange[`${scaleInfo.scalePosition}`].low
        ),
        maxScale: Number.parseFloat(
          scaleRange[`${scaleInfo.scalePosition}`].high
        ),
        color: Object.values(COLORS).find(
          (color) => color.key === ((i + 1) % 51).toFixed(0)
        ).color,
        state: true,
        none: false,
        dataUnit: channelInfo.dataUnit,
      };
    });

    /** Create an array of values */
    Object.entries(filteredChResponses).forEach(
      ([channelId, aggregatedData]) => {
        const target = cdata.find((c) => c.channelId === channelId);
        const newPdata = target.pdata;

        aggregatedData.forEach((point) => {
          const timestamp = dayjs(point.date || point.timestamp).valueOf();
          const tdataIndex = tdata.indexOf(timestamp);
          const insertValue =
            dataType !== 'raw-data' ? point[displayValue] : point.value;
          if (tdataIndex !== -1) newPdata[tdataIndex] = insertValue;
        });
      }
    );

    /** Data to be passed to the function that draws the graph */
    const graphDataSets = { tdata, cdata };
    yield put(
      Actions.fetchRecorderViewSuccess({
        data: graphDataSets,
      })
    );
  } catch (e) {
    console.log('e3', e);
    // When device id unregistered. Reload devices...
    if (e.status === 404) {
      const currentDevices = yield select((state) => state.UnitsChannels.units);
      const deviceIdUnregistered = e.data?.deviceIdUnregistered || '';
      const dv = currentDevices.find(
        (item) => item.deviceId === deviceIdUnregistered
      );
      const deviceName =
        dv && dv.name
          ? dv.name
          : `${TextConstants.Common.NoName}(id: ${deviceIdUnregistered})`;
      toastWarning(
        `${e.message}${TextConstants.Common.DeviceNotRegistered}${deviceName}。`
      );
      yield put(fetchUnits());
    } else if (e.status === 504) {
      // "Endpoint request timed out"
      yield put(
        Actions.fetchRecorderViewError({
          message: TextConstants.DataView.EndpointTimeOutError,
        })
      );
    } else {
      yield put(
        Actions.fetchRecorderViewError({
          message: e?.message || 'loading failed',
        })
      );
    }
  } finally {
    yield delay(2500);
    yield put(Actions.fetchRecorderViewCleanState());
  }
}

function* fetchRecorderViewUpdate({ payload }) {
  try {
    // check valid token
    yield checkValidToken();

    const { data, dataFrame } = payload;
    const { unit, selectedChannels } = dataFrame;

    yield put(backgroundFetchDeviceChannels({ deviceId: unit }));
    yield race({
      success: take(
        unitsChannelsActions.BACKGROUND_FETCH_DEVICE_CHANNELS_SUCCESS
      ),
      error: take(unitsChannelsActions.FETCH_DEVICE_CHANNELS_ERROR),
    });
    const { backgroundDeviceChannels } = yield select(getUnitChannels);
    const numberChannelIds = backgroundDeviceChannels
      .filter((channel) => channel.dataType === 0)
      .map((channel) => channel.channelId);
    const selectedNumberChannelIds = selectedChannels.filter((channelId) =>
      numberChannelIds.includes(channelId)
    );
    const selectedNumberChannelsInfo = backgroundDeviceChannels.filter(
      (channel) => selectedNumberChannelIds.includes(channel.channelId)
    );

    const to = dayjs().format('YYYY/MM/DD');
    const from = dayjs().subtract(1, 'hours').format('YYYY/MM/DD');
    const endTime = dayjs().format('HH:mm');
    const startTime = dayjs().subtract(1, 'hours').format('HH:mm');

    const startParam = from + ' ' + startTime;
    const endParam = to + ' ' + endTime;
    const dateFormat = 'YYYY-MM-DD HH:mm';

    const url = `${ApiConstants.BASE_URL}${
      ApiConstants.RECORDER
    }?type=raw&start=${dayjs(startParam).format(dateFormat)}&end=${dayjs(
      endParam
    ).format(
      dateFormat
    )}&deviceId=${unit}&channelIds=${selectedNumberChannelIds.join(
      '&channelIds='
    )}`;

    /** Response */
    const chResponses = yield call(requests.getRequest, url, {});

    /** Timestamp array */
    const tArray = [];
    const filteredChResponses = Object.fromEntries(
      Object.entries(chResponses).filter(([channelId, _]) =>
        selectedNumberChannelIds.includes(channelId)
      )
    );
    Object.entries(filteredChResponses).forEach(([_, aggregatedData]) => {
      aggregatedData.forEach((d) => {
        const t = dayjs(d.date || d.timestamp).valueOf();
        tArray.push(t);
      });
    });

    /** Data for graph display of additional time */
    const addTdata = Array.from(new Set(tArray))
      .sort((a, b) => (a > b ? -1 : 1))
      .filter((t) => !data.tdata.includes(t));
    const tdata = [...addTdata, ...data.tdata];

    const cdata = structuredClone(data.cdata);
    cdata.forEach((c) => {
      const channelInfo = selectedNumberChannelsInfo.find(
        (channel) => channel.channelId === c.channelId
      );
      c.channelName = channelInfo?.channelName ?? '';
      c.dataUnit = channelInfo?.dataUnit ?? '';
      c.none = !selectedNumberChannelIds.includes(c.channelId);
    });

    /** Create an array of values */
    Object.entries(filteredChResponses).forEach(
      ([channelId, aggregatedData]) => {
        const target = cdata.find((c) => c.channelId === channelId);
        const newPdata = target?.pdata;
        if (!newPdata) return;

        aggregatedData.forEach((point) => {
          const timestamp = dayjs(point.timestamp).valueOf();
          const tdataIndex = tdata.indexOf(timestamp);
          const insertValue = point.value;
          if (tdataIndex !== -1) newPdata[tdataIndex] = insertValue;
        });
      }
    );

    while (tdata.length > POINT_MAX) {
      tdata.pop();
      cdata.forEach((c) => c.pdata.pop());
    }

    /** Data to be passed to the function that draws the graph */
    const graphDataSets = { tdata, cdata };
    yield put(
      Actions.fetchRecorderViewUpdateSuccess({
        data: graphDataSets,
      })
    );
  } catch (e) {
    console.log('e3', e);
    // When device id unregistered. Reload devices...
    if (e.status === 404) {
      const currentDevices = yield select((state) => state.UnitsChannels.units);
      const deviceIdUnregistered = e.data?.deviceIdUnregistered || '';
      const dv = currentDevices.find(
        (item) => item.deviceId === deviceIdUnregistered
      );
      const deviceName =
        dv && dv.name
          ? dv.name
          : `${TextConstants.Common.NoName}(id: ${deviceIdUnregistered})`;
      toastWarning(
        `${e.message}${TextConstants.Common.DeviceNotRegistered}${deviceName}。`
      );
      yield put(fetchUnits());
    } else if (e.status === 504) {
      // "Endpoint request timed out"
      yield put(
        Actions.fetchRecorderViewError({
          message: TextConstants.DataView.EndpointTimeOutError,
        })
      );
    } else {
      yield put(
        Actions.fetchRecorderViewError({
          message: e?.message || 'loading failed',
        })
      );
    }
  }
}

export function* watchRecorderView() {
  yield takeLatest(
    Actions.RecorderViewActions.FETCH_RECORDER_VIEW,
    fetchRecorderView
  );
}

export function* watchRecorderViewUpdate() {
  yield takeLeading(
    Actions.RecorderViewActions.FETCH_RECORDER_VIEW_UPDATE,
    function* (action) {
      yield race({
        response: call(fetchRecorderViewUpdate, { payload: action.payload }),
        cancel: take(Actions.RecorderViewActions.FETCH_RECORDER_VIEW),
      });
    }
  );
}

export default function* rootSaga() {
  yield all([fork(watchRecorderView), fork(watchRecorderViewUpdate)]);
}
